import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import TopBarCard from './components/TopBarCard.js';
import { Settings, LogOut } from 'lucide-react';
import { ReactComponent as BriefcaseIcon } from '../../assets/icons/commonIcons/BriefcaseIcon.svg';
import { ReactComponent as BellIcon } from '../../assets/icons/commonIcons/BellIcon.svg';
import { ReactComponent as HelpCircleIcon } from '../../assets/icons/commonIcons/HelpCircleIcon.svg';
import { ReactComponent as InviteIcon } from '../../assets/icons/commonIcons/InviteIcon.svg';
import NotificationPanel from '../notifications/NotificationPanel.jsx';
import { useUser } from '../../features/auth/hooks/useUser.js';
import { useAuth } from '../../features/auth/hooks/useAuth.js';
import { tokenService } from '../../shared/services/Index.js';
import { sessionManager } from '../../shared/services/sessionManagement/sessionManager';
import { loadPrismTheme } from '../../shared/utils/SyntaxColouring.js';
import UserProfileIcon from '../../assets/icons/menuIcons/UserProfileIcon.jsx';
import TopNavBtn from '../../assets/icons/navbarIcons/TopNavBtn.jsx';
import {ReactComponent as TonicIcon} from '../../assets/icons/tonicIcons/TonicIcon.svg';
import {ReactComponent as TonicLogo} from '../../assets/logos/tonicLogos/TonicLogo.svg';
import { useTheme } from '../../styles/ThemeContext.js';
import { useNotifications } from '../../features/organization/hooks/useNotifications.js';
import { useDashboardActions } from '../../store/dashboard/dashboardStore';
import { useGeneration } from './GenerationContext.js';
import AnalyzerNavigationBlocker from '../../features/qualityIntelligence/components/analyzer/DeepAnalyserComponent/AnalyzerNavigationBlocker';

const TopBar = memo(() => {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { logout } = useAuth();
  const { resetToGeneralDashboard } = useDashboardActions();
  const { isAnalyzerStreaming } = useGeneration();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const profileDropdownRef = useRef(null);
  const profileButtonRef = useRef(null);
  const { userData } = useUser();

  // Inactivity warning state — driven by SessionManager events (single source of truth)
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  const userId = userData?.userId;

  // Notification state
  const {
    notifications,
    loading,
    unreadCount,
    handleMarkAsRead,
    handleMarkAllAsRead
  } = useNotifications(userId);

  const organizationName = userData?.currentOrganisationName ||
                          (userData?.organizations?.[0]?.name) ||
                          'Workspace';

  useEffect(() => {
    // Close dropdown when clicking outside
    function handleClickOutside(event) {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target) &&
          profileButtonRef.current && !profileButtonRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    }

    // Close dropdown on scroll
    function handleScroll() {
      setIsProfileDropdownOpen(false);
    }

    // Close dropdown on window resize
    function handleResize() {
      setIsProfileDropdownOpen(false);
    }
 
    if (isProfileDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isProfileDropdownOpen]);

  // Listen for SessionManager events (single source of truth for inactivity)
  useEffect(() => {
    const handleSessionWarning = () => {
      setShowInactivityWarning(true);
    };

    const handleSessionExtended = () => {
      setShowInactivityWarning(false);
    };

    const handleSessionExpired = () => {
      setShowInactivityWarning(false);
      resetToGeneralDashboard();
    };

    window.addEventListener('sessionWarning', handleSessionWarning);
    window.addEventListener('sessionExtended', handleSessionExtended);
    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('sessionWarning', handleSessionWarning);
      window.removeEventListener('sessionExtended', handleSessionExtended);
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, [resetToGeneralDashboard]);
  
  const handleThemeToggle = () => {
    toggleDarkMode();
    loadPrismTheme(!isDarkMode);
  };

  const handleOpenInviteModal = () => {
    // Comprehensive authentication check
    const email = tokenService.getEmail();
    const token = tokenService.getToken();
 
    if (!email || !token) {
      navigate('/');
      return;
    }
 
    setIsModalOpen(true);
  };
 
  const handleInviteSuccess = (data) => {
    setIsModalOpen(false);
  };

  // Notification handlers
  const handleToggleNotificationPanel = () => {
    setIsNotificationPanelOpen(!isNotificationPanelOpen);
    // Close profile dropdown if open
    if (isProfileDropdownOpen) {
      setIsProfileDropdownOpen(false);
    }
  };
 
  const toggleProfileDropdown = () => {
    if (!isProfileDropdownOpen && profileButtonRef.current) {
      // Calculate position when opening
      const rect = profileButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8, // 8px gap below the button
        left: rect.right - 200, // Align right edge (assuming dropdown width is ~200px)
      });
    }
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
    if (isNotificationPanelOpen) {
      setIsNotificationPanelOpen(false);
    }
  };
 
  const confirmLogout = useCallback(() => {
    setShowLogoutWarning(false);
    setIsProfileDropdownOpen(false);
    resetToGeneralDashboard();
    logout();
  }, [resetToGeneralDashboard, logout]);

  const handleSignOut = useCallback(() => {
    if (isAnalyzerStreaming) {
      setIsProfileDropdownOpen(false);
      setShowLogoutWarning(true);
      return;
    }
    resetToGeneralDashboard();
    logout();
  }, [isAnalyzerStreaming, resetToGeneralDashboard, logout]);

  // Portal dropdown component
  const PortalDropdown = () => {
    if (!isProfileDropdownOpen) return null;

    return createPortal(
      <div 
        ref={profileDropdownRef}
        className="profile-dropdown-portal"
        style={{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          zIndex: 10100,
          minWidth: '200px',
        }}
      >
        <div className="profile-dropdown">
          <div className="profile-dropdown-header">
            <p className="user-name">{userData?.name || 'User'}</p>
            <p className="user-email">{userData?.email || tokenService.getEmail() || 'user@example.com'}</p>
          </div>
          <div className="profile-dropdown-items">
            <button 
              className="dropdown-item logout-item" 
              onClick={handleSignOut}
            >
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <TopBarCard transparent elevated className="topnav-container">
      <div className="top-bar-layout">
        <TonicIcon/>
        {/* <TonicLogo/> */}
        <div className="top-search-container">         
          <div className="icons-container">
            <label className="switch">
              <input
                type="checkbox"
                checked={isDarkMode}
                onChange={handleThemeToggle}
              />
              <span className="slider round"></span>
            </label>
            <button 
              className="nav-button body-text" 
              onClick={handleToggleNotificationPanel}
              title='Notifications'
            >
              <TopNavBtn icon={BellIcon} />
            </button>
            <div className="profile-dropdown-container">
              <button 
                ref={profileButtonRef}
                className="nav-button body-text"
                onClick={toggleProfileDropdown}
              >
                <UserProfileIcon
                  size={18}
                  letter={userData?.name || 'U'}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
 
      {/* Inactivity warning notification */}
      {showInactivityWarning && (
        <div className="inactivity-warning-notification">
          <div className="inactivity-warning-content">
            <p>You will be logged out due to inactivity soon.</p>
            <button
              onClick={() => sessionManager.extendSession()}
              className="stay-active-button"
            >
              Stay Active
            </button>
          </div>
        </div>
      )}

      {/* Portal dropdown rendered outside normal DOM hierarchy */}
      <PortalDropdown />

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={isNotificationPanelOpen}
        onClose={() => setIsNotificationPanelOpen(false)}
        notifications={notifications}
        onMarkAsRead={handleMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
        userId={userId}
      />

      <AnalyzerNavigationBlocker
        isOpen={showLogoutWarning}
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutWarning(false)}
        onLeaveAndNotify={null}
        title="Generation in Progress"
        message="Your generation is still in progress. If you log out now you will need to regenerate this step."
        confirmText="Log Out & Lose Progress"
        cancelText="Stay"
      />
    </TopBarCard>
  );
});

TopBar.displayName = 'TopBar';

export default TopBar;